import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { ArrowLeft, Search, Users, UserPlus, RefreshCw, Eye, Mail, Phone, Building2, MapPin, Calendar, LogIn } from "lucide-react";
import { toast } from "sonner";

interface Client {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  first_name: string;
  last_name: string;
  phone: string;
  company: string;
  street_address: string;
  city: string;
  province: string;
  postal_code: string;
  country: string;
}

const AdminClients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const url = `https://${projectId}.supabase.co/functions/v1/list-clients?search=${encodeURIComponent(search)}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Erreur");
      }

      setClients(result.clients || []);
    } catch (err: any) {
      toast.error(err.message || "Erreur lors du chargement des clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchClients();
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "Jamais";
    return new Date(dateStr).toLocaleDateString("fr-CA", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateShort = (dateStr: string | null) => {
    if (!dateStr) return "Jamais";
    return new Date(dateStr).toLocaleDateString("fr-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getFullAddress = (client: Client) => {
    const parts = [
      client.street_address,
      client.city,
      client.province,
      client.postal_code,
      client.country,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "—";
  };

  const DetailRow = ({ icon: Icon, label, value }: { icon: any; label: string; value: string }) => (
    <div className="flex items-start gap-3 py-3 border-b border-border last:border-0">
      <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium break-all">{value || "—"}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <NavBar />
      <main className="flex-1 container mx-auto px-4 py-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour
        </Button>

        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Gestion des clients ({clients.length})
              </CardTitle>
              <Button onClick={() => navigate("/admin/create-client")} size="sm">
                <UserPlus className="mr-2 h-4 w-4" />
                Nouveau client
              </Button>
            </div>

            <form onSubmit={handleSearch} className="flex gap-2 mt-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par nom, courriel, entreprise, téléphone..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button type="submit" variant="secondary" size="icon">
                <Search className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => { setSearch(""); setTimeout(fetchClients, 0); }}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </form>
          </CardHeader>

          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : clients.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">
                Aucun client trouvé
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead>Courriel</TableHead>
                      <TableHead className="hidden md:table-cell">Entreprise</TableHead>
                      <TableHead className="hidden md:table-cell">Téléphone</TableHead>
                      <TableHead className="hidden lg:table-cell">Ville</TableHead>
                      <TableHead className="hidden lg:table-cell">Inscrit le</TableHead>
                      <TableHead className="text-right">Détails</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clients.map((client) => (
                      <TableRow key={client.id} className="cursor-pointer" onClick={() => setSelectedClient(client)}>
                        <TableCell className="font-medium">
                          {client.first_name || client.last_name
                            ? `${client.first_name} ${client.last_name}`.trim()
                            : "—"}
                        </TableCell>
                        <TableCell className="text-sm">{client.email}</TableCell>
                        <TableCell className="hidden md:table-cell text-sm">
                          {client.company || "—"}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-sm">
                          {client.phone || "—"}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm">
                          {client.city ? `${client.city}, ${client.province}` : "—"}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm">
                          {formatDateShort(client.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); setSelectedClient(client); }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />

      {/* Client Detail Dialog */}
      <Dialog open={!!selectedClient} onOpenChange={(open) => !open && setSelectedClient(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {selectedClient?.first_name || selectedClient?.last_name
                ? `${selectedClient?.first_name} ${selectedClient?.last_name}`.trim()
                : "Client"}
            </DialogTitle>
          </DialogHeader>

          {selectedClient && (
            <div className="space-y-0">
              <DetailRow icon={Mail} label="Courriel" value={selectedClient.email} />
              <DetailRow icon={Phone} label="Téléphone" value={selectedClient.phone} />
              <DetailRow icon={Building2} label="Entreprise" value={selectedClient.company} />
              <div className="flex items-start gap-3 py-3 border-b border-border">
                <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div className="min-w-0 w-full space-y-1">
                  <p className="text-xs text-muted-foreground">Adresse</p>
                  <p className="text-sm font-medium">{selectedClient.street_address || "—"}</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    <div>
                      <p className="text-xs text-muted-foreground">Ville</p>
                      <p className="text-sm font-medium">{selectedClient.city || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Province</p>
                      <p className="text-sm font-medium">{selectedClient.province || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Code postal</p>
                      <p className="text-sm font-medium">{selectedClient.postal_code || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Pays</p>
                      <p className="text-sm font-medium">{selectedClient.country || "—"}</p>
                    </div>
                  </div>
                </div>
              </div>
              <DetailRow icon={Calendar} label="Date d'inscription" value={formatDate(selectedClient.created_at)} />
              <DetailRow icon={LogIn} label="Dernière connexion" value={formatDate(selectedClient.last_sign_in_at)} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminClients;